import React, { useCallback } from "react";
import { Control, UseFormSetValue, Path } from "react-hook-form";
import { useTranslation } from "@/hooks/useTranslation";
import { UseQueryResult, useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CreditCardFormData } from "@/networking/payments.api";
import Checkbox from "../ui/input/checkbox";
import { CartResponse } from "@/networking/carts.api";
import { useWatch } from "react-hook-form";
import NativeSelect from "../ui/input/native-select";
import {
  CountriesAndLanguages,
  fetchCountriesAndLanguages,
  AddressField,
} from "@/networking/addresses.api";
import useAutocomplete from "./hooks/useAutocomplete";
import { cn } from "@/lib/utils";
import VgsCreditCardForm from "./vgs-collect-card-form";
import { VgsFormState } from "@/hooks/useVgsCollect";
import DynamicAddressFields from "@/components/shared/dynamic-address-fields";
import { State } from "@/lib/country-config";
import { TestGatewayTooltip } from "./payment-method-select";

export default function CreditCardDialog({
  isOpen,
  control,
  onSubmit,
  handleClose,
  isSubmitting,
  setValue,
  saveCardError,
  query,
  cartToken,
  countries: countriesProp,
  isVgsFormReady,
  iframeLoadError,
  vgsFieldState,
  addressFields,
  stateOptions,
  handleCountryChange,
  isTestGateway,
}: {
  isOpen: boolean;
  control: Control<CreditCardFormData>;
  onSubmit: () => void;
  handleClose: (isClosed: boolean) => void;
  isSubmitting: boolean;
  setValue: UseFormSetValue<CreditCardFormData>;
  saveCardError: string | undefined;
  query?: UseQueryResult<CartResponse | null>;
  cartToken?: string;
  countries?: CountriesAndLanguages["countries"];
  usesVgsCollect?: boolean;
  isVgsFormReady?: boolean;
  iframeLoadError?: boolean;
  vgsFieldState?: VgsFormState;
  addressFields: AddressField[];
  stateOptions: State[];
  handleCountryChange: (
    countryCode: string,
    updateCart?: boolean,
  ) => Promise<void>;
  isTestGateway?: boolean;
}) {
  const { t, language } = useTranslation();
  const defaultPaymentMethod = useWatch({ control, name: "default" });
  const billingCountry = useWatch({ control, name: "billing_address.country" });

  const shippingAddress = query?.data?.cart?.ship_to;
  const hasShipTo = !!shippingAddress;

  const useShippingAsBilling =
    useWatch({ control, name: "use_shipping_address_for_billing" }) ?? false;

  const { data: countriesData } = useQuery({
    queryKey: ["countries-and-languages", cartToken, language],
    queryFn: () => fetchCountriesAndLanguages(cartToken || "", language),
    select: (data) => data.countries,
    enabled: !countriesProp && !!cartToken,
  });

  const countries = countriesProp || countriesData || [];

  // useAutocomplete branches on the literal string "United States" to pick
  // the US-specific SmartyStreets path. Keep that as the fallback so US
  // users opening the dialog before the countries query resolves still hit
  // the US autocomplete branch instead of the international one.
  const billingCountryName =
    countries.find((country) => country.iso === billingCountry)?.name ||
    "United States";

  const setBillingAddressFields = useCallback(
    (fields: {
      address1: string;
      address2: string;
      city: string;
      state: string;
      postal_code: string;
      country: string;
    }) => {
      setValue("billing_address.address1", fields.address1);
      setValue("billing_address.address2", fields.address2);
      setValue("billing_address.city", fields.city);
      setValue("billing_address.state", fields.state);
      setValue("billing_address.postal_code", fields.postal_code);
      setValue("billing_address.country", fields.country);
      handleCountryChange(fields.country, false);
    },
    [setValue, handleCountryChange],
  );

  const populateBillingFromShipping = useCallback(() => {
    if (!shippingAddress) return;
    setBillingAddressFields({
      address1: shippingAddress.address1 || "",
      address2: shippingAddress.address2 || "",
      city: shippingAddress.city || "",
      state: shippingAddress.state || "",
      postal_code: shippingAddress.postal_code || "",
      country: shippingAddress.country_code || "US",
    });
  }, [shippingAddress, setBillingAddressFields]);

  const clearBillingAddress = useCallback(() => {
    const countryToKeep =
      shippingAddress?.country_code || billingCountry || "US";
    setBillingAddressFields({
      address1: "",
      address2: "",
      city: "",
      state: "",
      postal_code: "",
      country: countryToKeep,
    });
  }, [shippingAddress, billingCountry, setBillingAddressFields]);

  const handleUseShippingAsBillingChange = useCallback(
    (checked: boolean) => {
      setValue("use_shipping_address_for_billing", checked, {
        shouldValidate: false,
      });
      if (checked) {
        populateBillingFromShipping();
      } else {
        clearBillingAddress();
      }
    },
    [setValue, populateBillingFromShipping, clearBillingAddress],
  );

  const {
    renderAutocompleteOptions,
    getAutocompleteOptions,
    handleOptionClick,
  } = useAutocomplete<CreditCardFormData>(
    setValue,
    "billing_address.city" as Path<CreditCardFormData>,
    "billing_address.state" as Path<CreditCardFormData>,
    "billing_address.postal_code" as Path<CreditCardFormData>,
    "billing_address.address1" as Path<CreditCardFormData>,
    stateOptions,
  );

  const handleBillingCountryChange = async (countryCode: string) => {
    await handleCountryChange(countryCode, false);
    setValue("billing_address.country", countryCode, {
      shouldValidate: true,
    });
    setValue("billing_address.address1", "");
    setValue("billing_address.address2", "");
    setValue("billing_address.city", "");
    setValue("billing_address.state", "");
    setValue("billing_address.postal_code", "");
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="credit-card-dialog z-[100] max-h-[90vh] max-w-sm overflow-y-auto rounded md:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-md flex items-center gap-2 font-medium text-gray-900">
            {t("add_credit_card")}
            {isTestGateway && (
              <TestGatewayTooltip
                title={t("test_gateway")}
                description={t("test_gateway_tooltip")}
              />
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="rounded shadow-xs">
          <VgsCreditCardForm
            isFormReady={isVgsFormReady || false}
            iframeLoadError={iframeLoadError}
            vgsFieldState={vgsFieldState || {}}
            networkError={saveCardError}
          />

          {hasShipTo && (
            <div
              className={cn(
                "h-input flex w-full items-center px-4 ring-1 ring-gray-300",
                useShippingAsBilling ? "rounded-b" : "",
              )}
            >
              <Checkbox
                control={control}
                name="use_shipping_address_for_billing"
                id="use-shipping-address-for-billing-dialog"
                label={t("use_shipping_address_for_billing")}
                classNames="text-sm"
                onChange={(e) =>
                  handleUseShippingAsBillingChange(e.target.checked)
                }
              />
            </div>
          )}

          {(!useShippingAsBilling || !hasShipTo) && (
            <>
              <NativeSelect
                key={billingCountry || "no-country"}
                control={control}
                label={t("country")}
                options={
                  countries
                    ?.map((country) => ({
                      id: country.iso,
                      name: country.name,
                    }))
                    .sort((a, b) => {
                      if (a.id === billingCountry) return -1;
                      if (b.id === billingCountry) return 1;
                      return a.name.localeCompare(b.name);
                    }) || []
                }
                name="billing_address.country"
                onSelectChange={(e) => {
                  handleBillingCountryChange(e.id.toString());
                }}
                inputStyles=""
                autoComplete="billing country"
              />
              <DynamicAddressFields
                addressFields={addressFields}
                stateOptions={stateOptions}
                fieldPathPrefix="billing_address"
                control={control}
                formCountry={billingCountryName}
                countryCode={billingCountry}
                renderAutocompleteOptions={renderAutocompleteOptions}
                getAutocompleteOptions={getAutocompleteOptions}
                handleOptionClick={handleOptionClick}
                collectName={false}
              />
            </>
          )}
        </div>

        <DialogFooter className="flex flex-row items-center justify-between space-x-2">
          <div className="flex-2">
            <Checkbox
              control={control}
              name="default"
              id="default"
              label={t("set_as_default_payment_method")}
              classNames="text-sm"
              onChange={() => {
                setValue("default", !defaultPaymentMethod);
              }}
            />
          </div>
          <div className="flex-1 text-right">
            <Button
              type="button"
              onClick={onSubmit}
              disabled={isSubmitting || iframeLoadError}
              className="bg-brand-primary text-brand-primary-text"
            >
              {isSubmitting && (
                <div className="mr-3 h-5 w-5 animate-spin rounded-full border-4 border-t-4 border-white border-t-transparent"></div>
              )}
              {isSubmitting ? `${t("securing")}...` : t("save_card")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
